import ApplicationController from "./application_controller"
import Logger from '../lib/logger'
import { showToast as globalToast } from '../utils/toast'

/**
 * FormController - Base controller for reusable form functionality
 * Provides common form features that can be extended by entity-specific controllers
 */
export default class extends ApplicationController {
  static targets = [
    "form", "field", "textarea", "fileInput", "submitButton",
    "autoSaveStatus", "autoSaveMessage"
  ]
  static values = {
    debounceMs: Number,
    autoSaveEnabled: Boolean,
    entityType: String,
    maxFileSize: Number,
    allowedFileTypes: Array
  }

  connect() {
    super.connect()
    Logger.debug('FormController connecting', {
      entityType: this.entityTypeValue,
      debounceMs: this.debounceMsValue,
      autoSaveEnabled: this.autoSaveEnabledValue
    })
    this.debounceMsValue = this.debounceMsValue || 1000
    this.autoSaveEnabledValue = this.autoSaveEnabledValue !== false
    this.maxFileSizeValue = this.maxFileSizeValue || 10 * 1024 * 1024 // 10MB default
    this.allowedFileTypesValue = this.allowedFileTypesValue || ['.pdf', '.doc', '.docx', '.jpg', '.png']

    this.setupFormListeners()
    this.setupAutoResize()
    this.initializeFormState()
  }

  setupFormListeners() {
    // Auto-save listeners for all form fields
    this.element.querySelectorAll('input, textarea, select').forEach(field => {
      if (field.dataset.action && field.dataset.action.includes('autoSave')) {
        this.addEventListener(field, 'input', this.debounceAutoSave)
        this.addEventListener(field, 'change', this.debounceAutoSave)
      }
    })

    // Form submission handling
    const form = this.hasFormTarget ? this.formTarget : this.element
    this.addEventListener(form, 'submit', this.handleSubmit)

    // File validation
    if (this.hasFileInputTarget) {
      this.addEventListener(this.fileInputTarget, 'change', this.handleFileSelect)
    }
  }

  setupAutoResize() {
    // Auto-resize textareas
    this.element.querySelectorAll('textarea').forEach(textarea => {
      this.addEventListener(textarea, 'input', () => this.autoResizeTextarea(textarea))
      this.autoResizeTextarea(textarea) // Initial resize
    })
  }

  initializeFormState() {
    this.formState = {}
    this.restoreFormState()
  }

  restoreFormState() {
    const storageKey = this.getStorageKey()
    if (!storageKey) return

    const savedState = localStorage.getItem(storageKey)
    if (savedState) {
      try {
        const state = JSON.parse(savedState)
        // Only restore if saved within last hour
        if (Date.now() - state.timestamp < 3600000) {
          this.restoreFieldValues(state.data)
        }
      } catch (e) {
        Logger.warn('Could not restore form state', { error: e.message })
      }
    }
  }

  restoreFieldValues(data) {
    Object.keys(data).forEach(fieldName => {
      const field = this.element.querySelector(`[name="${fieldName}"]`)
      if (field && data[fieldName]) {
        field.value = data[fieldName]
      }
    })
  }

  debounceAutoSave(event) {
    if (!this.autoSaveEnabledValue) return

    // Clear existing timeout
    if (this.autoSaveTimeout) {
      clearTimeout(this.autoSaveTimeout)
    }

    // Show auto-save indicator
    this.showAutoSaveStatus('Saving...')

    // Set new timeout for auto-save
    this.autoSaveTimeout = setTimeout(() => {
      this.autoSave()
    }, this.debounceMsValue)
  }

  autoSave() {
    if (!this.autoSaveEnabledValue) return

    this.saveFormState()
    this.showAutoSaveStatus('Changes saved automatically')

    // Clear the status after 3 seconds
    setTimeout(() => {
      this.hideAutoSaveStatus()
    }, 3000)
  }

  saveFormState() {
    const storageKey = this.getStorageKey()
    if (!storageKey) return

    const formData = new FormData(this.getFormElement())
    const data = Object.fromEntries(formData.entries())

    const state = {
      data: data,
      timestamp: Date.now()
    }

    localStorage.setItem(storageKey, JSON.stringify(state))
  }

  getFormElement() {
    if (this.hasFormTarget) return this.formTarget
    return (this.element.tagName === 'FORM') ? this.element : this.element.closest('form') || this.element
  }

  getStorageKey() {
    // Override in subclasses to provide specific storage key
    return this.entityTypeValue ? `${this.entityTypeValue}_form_state` : null
  }

  autoResizeTextarea(textarea) {
    textarea.style.height = 'auto'
    textarea.style.height = textarea.scrollHeight + 'px'
  }

  handleFileSelect(event) {
    const file = event.target.files[0]
    if (file) {
      if (!this.validateFile(file)) {
        event.target.value = ''
        return false
      }
      this.showFileSuccess(file)
    }
    return true
  }

  validateFile(file) {
    const errors = []

    // Check file size
    if (file.size > this.maxFileSizeValue) {
      errors.push(`File size (${this.formatFileSize(file.size)}) exceeds maximum allowed size (${this.formatFileSize(this.maxFileSizeValue)})`)
    }

    // Check file type
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase()
    if (!this.allowedFileTypesValue.includes(fileExtension)) {
      errors.push(`File type ${fileExtension} is not allowed. Allowed types: ${this.allowedFileTypesValue.join(', ')}`)
    }

    if (errors.length > 0) {
      this.showToast(errors.join('<br>'), 'error')
      return false
    }

    return true
  }

  handleSubmit(event) {
    if (!this.validateForm()) {
      event.preventDefault()
      return false
    }

    // Clear auto-save state on successful submission
    const storageKey = this.getStorageKey()
    if (storageKey) {
      localStorage.removeItem(storageKey)
    }

    this.showAutoSaveStatus('Submitting form...')
    return true
  }

  // Remote submission aliases for compatibility
  submit(event) {
    if (event) event.preventDefault()
    this.getFormElement().requestSubmit()
  }

  submitForm(event) {
    if (event) event.preventDefault()
    this.getFormElement().requestSubmit()
  }

  envia(event) {
    if (event) event.preventDefault()
    this.getFormElement().requestSubmit()
  }

  validateForm() {
    const form = this.getFormElement()
    const requiredFields = form.querySelectorAll('[required]')
    let isValid = true
    const errors = []

    requiredFields.forEach(field => {
      if (!field.value.trim()) {
        this.highlightError(field)
        errors.push(`${this.getFieldLabel(field)} is required`)
        isValid = false
      } else {
        this.clearError(field)
      }
    })

    // Validate file if present
    if (this.hasFileInputTarget && this.fileInputTarget.files.length > 0) {
      const file = this.fileInputTarget.files[0]
      if (!this.validateFile(file)) {
        isValid = false
      }
    }

    if (!isValid) {
      this.showErrors(errors)
      this.scrollToFirstError()
    }

    return isValid
  }

  validateField(event) {
    const field = event.target || event.currentTarget
    
    // Only validate required fields
    if (field.hasAttribute('required')) {
      if (!field.value.trim()) {
        this.highlightError(field, `${this.getFieldLabel(field)} is required`)
      } else {
        this.clearError(field)
      }
    } else {
      // Clear error if not required (e.g. if field becomes non-required dynamically)
      this.clearError(field)
    }
    
    this.showCompletionProgress()
  }

  getFieldLabel(field) {
    const label = field.closest('.form-group')?.querySelector('label')?.textContent ||
      field.getAttribute('placeholder') ||
      field.name ||
      'Field'
    return label.replace('*', '').trim()
  }

  highlightError(field, message = null) {
    field.classList.add('is-invalid')
    field.classList.remove('is-valid')

    // Add or update error message
    let errorDiv = field.parentNode.querySelector('.invalid-feedback')
    if (!errorDiv) {
      errorDiv = document.createElement('div')
      errorDiv.className = 'invalid-feedback'
      field.parentNode.appendChild(errorDiv)
    }
    if (message) errorDiv.textContent = message
  }

  clearError(field) {
    field.classList.remove('is-invalid')
    field.classList.add('is-valid')

    // Remove error message
    const errorDiv = field.parentNode.querySelector('.invalid-feedback')
    if (errorDiv) errorDiv.remove()
  }

  showErrors(errors) {
    // Remove existing error alerts
    this.element.querySelectorAll('.alert-danger').forEach(alert => alert.remove())

    // Create error alert
    const errorAlert = document.createElement('div')
    errorAlert.className = 'alert alert-danger alert-dismissible fade show'
    errorAlert.innerHTML = `
      <i class="fas fa-exclamation-triangle me-2"></i>
      <strong>Please fix the following errors:</strong>
      <ul class="mb-0 mt-2">
        ${errors.map(error => `<li>${error}</li>`).join('')}
      </ul>
      <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
    `

    // Insert at the top of the form
    this.element.insertBefore(errorAlert, this.element.firstChild)

    // Auto-remove after 10 seconds
    setTimeout(() => {
      if (errorAlert.parentNode) {
        errorAlert.remove()
      }
    }, 10000)
  }

  scrollToFirstError() {
    const firstError = this.element.querySelector('.is-invalid')
    if (firstError) {
      firstError.scrollIntoView({ behavior: 'smooth', block: 'center' })
      firstError.focus()
    }
  }

  showAutoSaveStatus(message) {
    if (this.hasAutoSaveStatusTarget) {
      if (this.hasAutoSaveMessageTarget) {
        this.autoSaveMessageTarget.textContent = message
      }
      this.autoSaveStatusTarget.style.display = 'block'
    }
  }

  hideAutoSaveStatus() {
    if (this.hasAutoSaveStatusTarget) {
      this.autoSaveStatusTarget.style.display = 'none'
    }
  }

  showFileSuccess(file) {
    const message = `File "${file.name}" selected successfully (${this.formatFileSize(file.size)})`
    this.showToast(message, 'success')
  }

  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  showToast(message, type = 'info') {
    globalToast(message, type)
  }

  resetForm() {
    const form = this.getFormElement()
    form.reset()

    // Clear localStorage
    const storageKey = this.getStorageKey()
    if (storageKey) {
      localStorage.removeItem(storageKey)
    }

    // Reset textarea heights
    this.element.querySelectorAll('textarea').forEach(textarea => {
      textarea.style.height = 'auto'
    })

    // Show reset message
    this.showAutoSaveStatus('Form has been reset')
    setTimeout(() => {
      this.hideAutoSaveStatus()
    }, 3000)
  }

  getFormCompletion() {
    const form = this.getFormElement()
    const fields = form.querySelectorAll('input, textarea, select')
    let completedFields = 0

    fields.forEach(field => {
      if (field.value && field.value.trim()) {
        completedFields++
      }
    })

    return fields.length > 0 ? Math.round((completedFields / fields.length) * 100) : 0
  }

  showCompletionProgress() {
    const completion = this.getFormCompletion()
    const progressBar = this.element.querySelector('.completion-progress')

    if (progressBar) {
      const progressFill = progressBar.querySelector('.progress-bar')
      if (progressFill) {
        progressFill.style.width = `${completion}%`
        progressFill.textContent = `${completion}% Complete`

        // Update progress bar color based on completion
        progressFill.className = `progress-bar ${completion < 50 ? 'bg-danger' : completion < 100 ? 'bg-warning' : 'bg-success'}`
      }
    }
  }
}